import { Button, Card, Icon, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { EmptyState, Skeleton } from '@/components/States'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { relativeTime } from '@/lib/relative-time'
import checkout from '@/app/layouts/CheckoutLayout.module.css'

export function RecipientsScreen() {
  const repository = useRepository()
  const recipients = useQuery(() => repository.listRecipients(), [repository])
  const corridors = useQuery(() => repository.listCorridors(), [repository])

  const corridorOf = (id: string) => corridors.data?.find((item) => item.id === id)

  return (
    <>
      <PageHeader
        title="Recipients"
        subtitle="The people you send to. We check the account details once, then never ask again."
        actions={
          <Button variant="primary" size="md" iconStart="person_add">
            Add a recipient
          </Button>
        }
      />

      <BentoGrid>
        <BentoSpan span={3}>
          <Card tone="light">
            {recipients.status === 'loading' && <Skeleton height={220} />}
            {recipients.status === 'ready' && recipients.data?.length === 0 && (
              <EmptyState
                icon="person_off"
                title="No recipients yet"
                body="Add someone once and sending to them takes two taps."
              />
            )}
            <Stack gap={0}>
              {recipients.data?.map((person) => {
                const corridor = corridorOf(person.corridorId)
                return (
                  <div key={person.id} className={checkout.line}>
                    {corridor && <CurrencyBadge currency={corridor.currency} size={38} />}
                    <Stack grow gap={0}>
                      <Text variant="caption" weight="semibold">
                        {person.name}
                      </Text>
                      <Text variant="eyebrow" tone="secondary" truncate>
                        {corridor?.country} · {person.maskedAccount}
                      </Text>
                    </Stack>
                    {person.lastSent ? (
                      <Text variant="eyebrow" tone="tertiary">
                        Sent {relativeTime(person.lastSent)}
                      </Text>
                    ) : (
                      <Pill tone="neutral">Never sent</Pill>
                    )}
                    <Button variant="outline" size="sm" to="/transfers/send">
                      Send
                    </Button>
                  </div>
                )
              })}
            </Stack>
          </Card>
        </BentoSpan>

        <Card tone="dark">
          <Stack direction="row" align="center" gap={3}>
            <Icon name="verified_user" size={20} />
            <Text variant="caption" weight="semibold" tone="onDark">
              Why we check once
            </Text>
          </Stack>
          <Text variant="caption" tone="onDarkMuted" className="pb-mt-3">
            Banks in most corridors pay to the account number, not the name. We verify the pair the
            first time so a typo cannot send your money to a stranger — and after that, sending is
            two taps.
          </Text>
        </Card>
      </BentoGrid>
    </>
  )
}
